import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import * as React from "react";
import PublicationButton from "./PublicationButton";
import { ExperimentalMutant, NaturalVariant, Publication } from "../../../api/openapi-generator";
import { useCallback } from "react";

const dbSnpRegex = /(In )?(dbSNP:rs([0-9][0-9]*))/;

const expasyVariantUrl = {
  start: 'https://web.expasy.org/variant_pages/',
  end: '.html'
}

const variantBody = (data: NaturalVariant) => {
  return <a href={`${expasyVariantUrl.start}${data.id}${expasyVariantUrl.end}`} target="_blank">{data.id}</a>
}

function KnownMutantsTable<T extends ExperimentalMutant>({
  sequence,
  mutants,
  naturalVariants = false,
  allPublications,
}: {
  sequence: string,
  mutants: T[],
  naturalVariants?: boolean,
  allPublications: Record<string, Publication>,
}) {

  const descriptionBody = useCallback((data: ExperimentalMutant) => {
    return <span>
      {sequence[data.position - 1]} {'->'} {data.targetAA}: {data.description}
      <PublicationButton publications={data.publications} allPublications={allPublications} small />
    </span>
  }, [sequence, allPublications])

  const descriptionBodyForNaturalVariants = useCallback((data: NaturalVariant) => {
    const description = data.description.split('; ')
      .filter(val => val.match(dbSnpRegex) === null) // filter out dbSNP references
      .map(x => x.trim())
      .map((value, index) => index === 0 && value.startsWith('In') ? (value.slice(0, 1).toLowerCase() + value.slice(1)) : value)
      .join('; ');
    const space1 = description.length > 0 ? ' ' : '';
    const period = description.length > 0 && description[description.length - 1] !== '.' ? '.' : '';

    const match = data.description.match(dbSnpRegex);
    const dbSnpLink = match !== null ? <>
      Corresponds to variant{' '}
      <a href={`https://www.ncbi.nlm.nih.gov/SNP/snp_ref.cgi?type=rs&rs=${match[3]}`} target="_blank">{match[2]}</a>
    </> : null;

    const space2 = data.publications.length === 0 && dbSnpLink !== null ? ' ' : '';

    return <span>
      <a href={`${expasyVariantUrl.start}${data.id}${expasyVariantUrl.end}`} target="_blank">
        {sequence[data.position - 1]} {'->'} {data.targetAA}
      </a>
      {space1}
      {description}
      {period}
      <PublicationButton publications={data.publications} allPublications={allPublications} small />
      {space2}
      {dbSnpLink}
    </span>
  }, [sequence, allPublications]);

  return (
    <DataTable
      value={mutants}
      // dataKey="id"
      responsiveLayout="scroll"  // "scroll" to avoid stacking
      className="p-datatable-sm ll-known-mutants-table"
      loading={false}
    >
      {naturalVariants && <Column field="id" body={variantBody} header="Variant" style={{ flex: '0 0 15%' }} />}
      <Column field="position" header="Position" style={{ flex: '0 0 10%' }} />
      <Column body={naturalVariants ? descriptionBodyForNaturalVariants : descriptionBody} header="Description" />
    </DataTable>
  )

}

export default KnownMutantsTable;
